import { IconChevronDown } from '@developerz/ui';
import { createSignal, createUniqueId, type JSX, Show } from 'solid-js';
import { t } from '../../i18n';
import { useCardControls } from '../../lib/card-controls-context';
import type { UtilBox } from '../../lib/fleetUtilization';
import { BLOCKER_TAG_LABEL, type CardView } from '../../lib/machine-card';
import { boxJobs } from '../../lib/machine-jobs';
import type { StartingRow } from '../../lib/send-work';
import { ControlLock } from './ControlLock';
import { MachineJobs } from './MachineJobs';
import { MoveNote } from './MoveNote';
import { MoveWorkButton } from './MoveWorkButton';
import { PauseAllButton } from './PauseAllButton';
import { SendWorkButton } from './SendWorkButton';
import { StartingRows } from './StartingRows';

// Now (#6785; the jobs list #6745, prototype zone header `:2459`, nowToggle `:2279-2283`):
// the header counts the machine's jobs ("Coder" for one, "N jobs", "N jobs, last known"
// offline) and, at two or more, carries the "Hide jobs" / "Show jobs" toggle; the body is
// `MachineJobs`, or the status sentence when the machine holds no job. A blocker on any of
// its jobs adds its Blocked or Parked tag below.
//
// THE JOB CONTROLS (#6753): for an admin on an own-org machine the header carries "Pause all"
// / "Resume all" with two or more controllable jobs (prototype allBtn, `:2284`), the page
// lock's countdown line sits above the jobs, and each job carries its own controls.
//
// SEND WORK (#6770): for an admin on an own-org machine the header carries "Send" (the
// prototype's sendBtn, `:2275`), and the jobs sent here that the machine has not started
// read "Starting" below the jobs. A read-only card passes no `send` and shows neither.
//
// MOVE WORK (#6772): beside Send, "Move" opens the card's Move work panel (`work`, passed only
// where it can open), and a job move's note with its Undo shows under the jobs (`MoveNote`).

/** What Now shows of Send work on a writable card. */
export type NowSend = {
  readonly offline: boolean;
  readonly expanded: boolean;
  readonly onOpen: () => void;
  readonly starting: readonly StartingRow[];
};

/** Now's Move button (#6772): present only where Move work can open. */
export type NowWork = { readonly expanded: boolean; readonly onOpen: () => void };

export function NowZone(props: {
  readonly box: UtilBox;
  readonly view: CardView;
  readonly nowMs: number;
  readonly send?: NowSend | undefined;
  readonly work?: NowWork | undefined;
}): JSX.Element {
  const now = (): CardView['now'] => props.view.now;
  const listId = createUniqueId();
  const [folded, setFolded] = createSignal(false);
  const many = (): boolean => boxJobs(props.box).length >= 2;
  const controls = useCardControls();
  // A Pause all / Resume all that failed outright (a 5xx, the network) says so here.
  const allRefusal = () =>
    controls?.on() === true ? controls.card.refusal(props.box.id) : undefined;
  return (
    <section class="mission-zone" aria-label={t('fleet.machines.card_now')} data-move-zone="now">
      <p class="mission-zone__label">
        <span>{t('fleet.machines.card_now')}</span>
        <span class="mission-zone__side">
          <Show when={now().count}>
            {(count) => <span class="mission-jobs__count">{count()}</span>}
          </Show>
          <PauseAllButton boxId={props.box.id} />
          <Show when={props.send}>
            {(send) => (
              <SendWorkButton
                variant="now"
                offline={send().offline}
                expanded={send().expanded}
                onOpen={send().onOpen}
              />
            )}
          </Show>
          <Show when={props.work}>
            {(work) => (
              <MoveWorkButton variant="now" expanded={work().expanded} onOpen={work().onOpen} />
            )}
          </Show>
          <Show when={many()}>
            <button
              type="button"
              class="mission-jobs__toggle"
              aria-expanded={!folded()}
              aria-controls={listId}
              aria-label={t(folded() ? 'fleet.machines.job_show' : 'fleet.machines.job_hide')}
              title={t(folded() ? 'fleet.machines.job_show' : 'fleet.machines.job_hide')}
              onClick={() => setFolded((f) => !f)}
            >
              <IconChevronDown class="mission-jobs__chev" size="0.75rem" />
            </button>
          </Show>
        </span>
      </p>
      <ControlLock />
      <Show when={allRefusal()}>
        {(r) => (
          <p class="mission-ctl__refusal" role="status">
            {r().text}
          </p>
        )}
      </Show>
      <Show when={now().hasJob} fallback={<p class="mission-card__now">{now().status}</p>}>
        <MachineJobs box={props.box} nowMs={props.nowMs} folded={folded()} listId={listId} />
      </Show>
      <MoveNote zone="now" />
      <Show when={now().tag}>
        {(tag) => (
          <span class="mission-card__tag" data-tag={tag()}>
            {t(BLOCKER_TAG_LABEL[tag()])}
          </span>
        )}
      </Show>
      <Show when={(props.send?.starting.length ?? 0) > 0}>
        <StartingRows rows={props.send?.starting ?? []} />
      </Show>
    </section>
  );
}
